import React, { useState, useEffect, useCallback } from "react";
import { api, useBusiness } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const formatCurrency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);

const HomeOfficeSetup = () => {
  const { selectedBusiness } = useBusiness();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    square_feet_office: "", square_feet_home: "",
    rent: "", mortgage_interest: "", utilities: "", internet: "", insurance: "", repairs: "",
    simplified_method: true
  });

  const fetchProfile = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const res = await api.get(`/home-office-profile/${selectedBusiness.id}`);
      if (res.data) {
        setProfile(res.data);
        setForm({
          square_feet_office: res.data.square_feet_office?.toString() || "",
          square_feet_home: res.data.square_feet_home?.toString() || "",
          rent: res.data.rent?.toString() || "", mortgage_interest: res.data.mortgage_interest?.toString() || "",
          utilities: res.data.utilities?.toString() || "", internet: res.data.internet?.toString() || "",
          insurance: res.data.insurance?.toString() || "", repairs: res.data.repairs?.toString() || "",
          simplified_method: res.data.simplified_method ?? true
        });
      }
    } catch { /* no profile yet */ }
    finally { setLoading(false); }
  }, [selectedBusiness]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const saveProfile = async () => {
    if (!form.square_feet_office || !form.square_feet_home) { toast.error("Office and home square footage are required"); return; }
    setSaving(true);
    try {
      const res = await api.post("/home-office-profile", {
        business_id: selectedBusiness.id,
        square_feet_office: parseFloat(form.square_feet_office),
        square_feet_home: parseFloat(form.square_feet_home),
        rent: form.rent ? parseFloat(form.rent) : null,
        mortgage_interest: form.mortgage_interest ? parseFloat(form.mortgage_interest) : null,
        utilities: form.utilities ? parseFloat(form.utilities) : null,
        internet: form.internet ? parseFloat(form.internet) : null,
        insurance: form.insurance ? parseFloat(form.insurance) : null,
        repairs: form.repairs ? parseFloat(form.repairs) : null,
        simplified_method: form.simplified_method
      });
      setProfile(res.data);
      toast.success("Home office profile saved");
    } catch { toast.error("Failed to save profile"); }
    finally { setSaving(false); }
  };

  const up = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (!selectedBusiness) return <div className="flex items-center justify-center min-h-[400px]"><p className="text-muted-foreground">Please select a business.</p></div>;
  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;

  const officeSqFt = parseFloat(form.square_feet_office) || 0;
  const homeSqFt = parseFloat(form.square_feet_home) || 1;
  const pct = (officeSqFt / homeSqFt * 100).toFixed(1);
  const simplifiedDeduction = Math.min(officeSqFt, 300) * 5;
  const totalExpenses = [form.rent, form.mortgage_interest, form.utilities, form.internet, form.insurance, form.repairs]
    .reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const regularDeduction = totalExpenses * (officeSqFt / homeSqFt);

  return (
    <div className="space-y-6" data-testid="home-office-page">
      <div>
        <h1 className="text-3xl font-bold text-stone-900">Home Office</h1>
        <p className="text-stone-600">Configure your home office for maximum deduction</p>
      </div>

      {profile && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card className="border-[#1e3a5f]/20 bg-[#1e3a5f]/5"><CardContent className="pt-5 pb-4 text-center">
            <p className="text-2xl font-bold text-[#1e3a5f]">{formatCurrency(profile.estimated_deduction)}</p>
            <p className="text-xs text-[#1e3a5f] mt-1">Estimated Deduction</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5 pb-4 text-center">
            <p className="text-2xl font-bold text-stone-800">{profile.business_use_pct}%</p>
            <p className="text-xs text-stone-500 mt-1">Business Use</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5 pb-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{profile.simplified_method ? "Simplified" : "Regular"}</p>
            <p className="text-xs text-stone-500 mt-1">Method</p>
          </CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Office Space</CardTitle>
          <CardDescription>Enter your home and office dimensions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Office Square Feet *</Label>
              <Input type="number" placeholder="150" value={form.square_feet_office} onChange={e => up("square_feet_office", e.target.value)} data-testid="office-sqft" />
            </div>
            <div className="space-y-2">
              <Label>Total Home Square Feet *</Label>
              <Input type="number" placeholder="2000" value={form.square_feet_home} onChange={e => up("square_feet_home", e.target.value)} data-testid="home-sqft" />
            </div>
          </div>
          {officeSqFt > 0 && homeSqFt > 0 && (
            <p className="text-sm text-stone-600">Business use: <span className="font-semibold">{pct}%</span> of your home</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Calculation Method</CardTitle>
              <CardDescription>Choose simplified ($5/sq ft, max 300) or regular (actual expenses)</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${form.simplified_method ? "text-[#1e3a5f] font-medium" : "text-stone-400"}`}>Simplified</span>
              <Switch checked={!form.simplified_method} onCheckedChange={c => up("simplified_method", !c)} data-testid="method-toggle" />
              <span className={`text-sm ${!form.simplified_method ? "text-blue-600 font-medium" : "text-stone-400"}`}>Regular</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {form.simplified_method ? (
            <div className="p-4 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
              <p className="text-sm text-[#1e3a5f]">
                <span className="font-bold">{formatCurrency(simplifiedDeduction)}</span> estimated deduction
                ({Math.min(officeSqFt, 300)} sq ft x $5/sq ft{officeSqFt > 300 ? ", capped at 300 sq ft" : ""})
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-stone-600 mb-3">Enter your annual home expenses. We'll apply your {pct}% business use percentage.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[["rent", "Rent"], ["mortgage_interest", "Mortgage Interest"], ["utilities", "Utilities"], ["internet", "Internet"], ["insurance", "Insurance"], ["repairs", "Repairs"]].map(([key, label]) => (
                  <div key={key} className="space-y-2">
                    <Label>{label} ($)</Label>
                    <Input type="number" step="0.01" placeholder="0.00" value={form[key]} onChange={e => up(key, e.target.value)} data-testid={`expense-${key}`} />
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-800">
                  Total expenses: {formatCurrency(totalExpenses)} x {pct}% = <span className="font-bold">{formatCurrency(regularDeduction)}</span> estimated deduction
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Side-by-side comparison */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Method Comparison</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className={`p-4 rounded-lg border-2 ${form.simplified_method ? "border-[#1e3a5f] bg-[#1e3a5f]/5" : "border-stone-200"}`}>
              <p className="text-sm font-medium text-stone-700">Simplified Method</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(simplifiedDeduction)}</p>
              <p className="text-xs text-stone-500 mt-1">$5/sq ft, max 300 sq ft</p>
            </div>
            <div className={`p-4 rounded-lg border-2 ${!form.simplified_method ? "border-blue-400 bg-blue-50" : "border-stone-200"}`}>
              <p className="text-sm font-medium text-stone-700">Regular Method</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(regularDeduction)}</p>
              <p className="text-xs text-stone-500 mt-1">Actual expenses x {pct}%</p>
            </div>
          </div>
          {regularDeduction > simplifiedDeduction && !form.simplified_method && (
            <p className="text-sm text-[#1e3a5f] mt-3 font-medium">The regular method saves you {formatCurrency(regularDeduction - simplifiedDeduction)} more!</p>
          )}
          {simplifiedDeduction > regularDeduction && form.simplified_method && (
            <p className="text-sm text-[#1e3a5f] mt-3 font-medium">The simplified method saves you {formatCurrency(simplifiedDeduction - regularDeduction)} more!</p>
          )}
        </CardContent>
      </Card>

      <Button onClick={saveProfile} disabled={saving} className="w-full bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="save-home-office-btn">
        {saving ? "Saving..." : "Save Home Office Profile"}
      </Button>
    </div>
  );
};

export default HomeOfficeSetup;
